export type FamsLocale = 'zh-CN' | 'zh-TW' | 'en-US' | 'ja-JP';

type LocalizedText = Record<FamsLocale, string>;

export type FamsPageDefinition = {
  key: string;
  slug: string;
  icon: string;
  title: LocalizedText;
  subtitle: LocalizedText;
  component?: string;
  hiddenInMenu?: boolean;
  access?: string;
};

export type FamsMenuGroupDefinition = {
  type: 'group';
  key: string;
  icon: string;
  title: LocalizedText;
  children: FamsPageDefinition[];
};

export type FamsMenuItem = FamsPageDefinition | FamsMenuGroupDefinition;

export type FamsSectionDefinition = {
  key: string;
  slug: string;
  icon: string;
  title: LocalizedText;
  children: FamsMenuItem[];
  flattenInMenu?: boolean;
};

export type FamsPageMatch = {
  section: FamsSectionDefinition;
  page: FamsPageDefinition;
  pageKey: string;
  path: string;
};

export type FamsRouteDefinition = {
  path: string;
  name?: string;
  icon?: string;
  component?: string;
  redirect?: string;
  hideInMenu?: boolean;
  access?: string;
  routes?: FamsRouteDefinition[];
};

const localized = (
  zhCN: string,
  zhTW: string,
  enUS: string,
  jaJP: string,
): LocalizedText => ({
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'en-US': enUS,
  'ja-JP': jaJP,
});

const createPage = (
  key: string,
  slug: string,
  icon: string,
  title: LocalizedText,
  subtitle: LocalizedText,
  component?: string,
  hiddenInMenu = false,
  access?: string,
): FamsPageDefinition => ({
  key,
  slug,
  icon,
  title,
  subtitle,
  component,
  hiddenInMenu,
  access,
});

const createSection = (
  key: string,
  slug: string,
  icon: string,
  title: LocalizedText,
  children: FamsMenuItem[],
  flattenInMenu = false,
): FamsSectionDefinition => ({
  key,
  slug,
  icon,
  title,
  children,
  flattenInMenu,
});

const createGroup = (
  key: string,
  icon: string,
  title: LocalizedText,
  children: FamsPageDefinition[],
): FamsMenuGroupDefinition => ({
  type: 'group',
  key,
  icon,
  title,
  children,
});

export const isFamsMenuGroup = (
  item: FamsMenuItem,
): item is FamsMenuGroupDefinition => 'type' in item && item.type === 'group';

export const getSectionPages = (
  section: FamsSectionDefinition,
): FamsPageDefinition[] =>
  section.children.flatMap((item) =>
    isFamsMenuGroup(item) ? item.children : [item],
  );

const _legacyFamsMenuSections: FamsSectionDefinition[] = [
  createSection(
    'home',
    'home',
    'home',
    localized('首页', '首頁', 'Home', 'ホーム'),
    [
      createPage(
        'operationsOverview',
        'operations-overview',
        'fundProjectionScreen',
        localized('经营总览', '經營總覽', 'Operations Overview', '経営概況'),
        localized(
          '集中展示基金会投资资产的整体经营情况，包括总市值、财务本金、浮盈、实现收益、投资收益率等核心指标，帮助管理层快速掌握全局。',
          '集中展示基金會投資資產的整體經營情況，包括總市值、財務本金、浮盈、實現收益、投資收益率等核心指標，幫助管理層快速掌握全局。',
          'View the foundation investment portfolio at a glance, including market value, principal, unrealized gains, realized income, and return metrics.',
          '基金会の投資資産全体を俯瞰し、総時価、元本、含み損益、実現収益、投資収益率などの主要指標を確認します。',
        ),
        './fams/operations-overview',
      ),
      createPage(
        'annualDashboard',
        'annual-dashboard',
        'dashboard',
        localized(
          '年度仪表盘',
          '年度儀表盤',
          'Annual Dashboard',
          '年度ダッシュボード',
        ),
        localized(
          '围绕本年度视角展示关键经营数据、净值曲线、资产配置和市值增长拆分，是系统首页的核心分析页面。',
          '圍繞本年度視角展示關鍵經營數據、淨值曲線、資產配置和市值增長拆分，是系統首頁的核心分析頁面。',
          'Track the current year through headline metrics, NAV trends, allocation mix, and market value growth drivers.',
          '当年度の主要データ、基準価額推移、資産配分、市場価値成長の内訳を集中的に表示するホームの中核分析画面です。',
        ),
        './fams/annual-dashboard',
      ),
      createPage(
        'longTermPerformance',
        'long-term-performance',
        'lineChart',
        localized(
          '长期绩效',
          '長期績效',
          'Long-term Performance',
          '長期パフォーマンス',
        ),
        localized(
          '展示三年、五年、成立以来等长周期绩效表现，包括投资收益率、实现收益率、累计收益和年度对比分析。',
          '展示三年、五年、成立以來等長周期績效表現，包括投資收益率、實現收益率、累計收益和年度對比分析。',
          'Review performance over multi-year horizons, including investment return, realized return, cumulative gains, and yearly comparisons.',
          '3年、5年、設定来などの長期パフォーマンスを確認し、投資収益率、実現収益率、累積収益、年度比較を把握します。',
        ),
      ),
      createPage(
        'dataStatus',
        'data-status',
        'database',
        localized('数据状态', '數據狀態', 'Data Status', 'データ状況'),
        localized(
          '展示最近一个交易日的数据齐备情况、采集状态、估值表处理状态和异常信息，帮助用户确认分析结果是否建立在完整数据之上。',
          '展示最近一個交易日的數據齊備情況、採集狀態、估值表處理狀態和異常資訊，幫助用戶確認分析結果是否建立在完整數據之上。',
          'Check whether the latest trading-day data, ingestion jobs, valuation sheets, and exception items are complete before analysis.',
          '直近取引日のデータ充足状況、収集状況、評価表処理状況、異常情報を確認し、分析結果の前提を点検します。',
        ),
      ),
      createPage(
        'riskAlerts',
        'risk-alerts',
        'warning',
        localized('风险预警', '風險預警', 'Risk Alerts', 'リスクアラート'),
        localized(
          '汇总系统当前触发的风险预警事项，包括波动异常、VaR 超阈值、集中度偏高等信息，便于快速发现问题并进入处置流程。',
          '匯總系統當前觸發的風險預警事項，包括波動異常、VaR 超閾值、集中度偏高等資訊，便於快速發現問題並進入處置流程。',
          'Summarize active alerts such as abnormal volatility, VaR breaches, and concentration issues so teams can act quickly.',
          '変動率異常、VaR閾値超過、集中度上昇など、現在発生しているリスクアラートを一覧で確認します。',
        ),
        './fams/risk-alerts',
      ),
    ],
  ),
  createSection(
    'assetAllocation',
    'asset-allocation',
    'pieChart',
    localized('资产配置', '資產配置', 'Asset Allocation', '資産配分'),
    [
      createPage(
        'allocationOverview',
        'allocation-overview',
        'appstore',
        localized(
          '配置总览',
          '配置總覽',
          'Allocation Overview',
          '配分サマリー',
        ),
        localized(
          '从整体视角展示当前资产配置结构，包括大类资产、一级与二级分类、穿透后配置结果及配置偏离情况。',
          '從整體視角展示當前資產配置結構，包括大類資產、一級與二級分類、穿透後配置結果及配置偏離情況。',
          'Review the current allocation structure across asset classes, category layers, look-through positions, and allocation drifts.',
          '現在の資産配分構造を俯瞰し、大分類・中分類・ルックスルー後の結果や乖離状況を確認します。',
        ),
        './fams/allocation-overview',
        true,
      ),
      createPage(
        'allocationDashboard',
        'allocation-dashboard',
        'dotChart',
        localized(
          '配置驾驶舱',
          '配置駕駛艙',
          'Allocation Cockpit',
          '配分コックピット',
        ),
        localized(
          '围绕 PKU 层级对象展示当前结构、下级贡献、偏离重点和底层资产暴露，支持连续下钻分析。',
          '圍繞 PKU 層級對象展示當前結構、下級貢獻、偏離重點和底層資產暴露，支持連續下鑽分析。',
          'Analyze structure, child contribution, drift highlights, and underlying exposure for PKU hierarchy objects in one cockpit.',
          'PKU 階層オブジェクトの構造、下位寄与、乖離、底層エクスポージャーを一画面で連続分析します。',
        ),
        './fams/allocation-dashboard',
      ),
      createPage(
        'strategicAllocation',
        'strategic-allocation',
        'compass',
        localized(
          '战略配置',
          '戰略配置',
          'Strategic Allocation',
          '戦略的資産配分',
        ),
        localized(
          '用于查看和管理基金会战略资产配置目标、配置区间、约束条件和长期配置原则。',
          '用於查看和管理基金會戰略資產配置目標、配置區間、約束條件和長期配置原則。',
          'Manage long-term target allocation ranges, strategic constraints, and governing allocation principles.',
          '基金会の戦略的な資産配分目標、配分レンジ、制約条件、長期方針を管理します。',
        ),
        './fams/strategic-allocation',
      ),
      createPage(
        'assetClassManagement',
        'asset-class-management',
        'cluster',
        localized(
          '资产类别管理',
          '資產類別管理',
          'Asset Class Management',
          '資産クラス管理',
        ),
        localized(
          '维护资产类别定义、分类层级和标签体系，为配置统计、穿透分析和报表输出提供统一基础。',
          '維護資產類別定義、分類層級和標籤體系，為配置統計、穿透分析和報表輸出提供統一基礎。',
          'Maintain asset class definitions, hierarchies, and tagging standards that support allocation analytics and reporting.',
          '資産クラスの定義、階層、タグ体系を整備し、配分分析やレポート出力の基盤を支えます。',
        ),
        './fams/asset-class-management',
      ),
      createPage(
        'pkuHierarchyView',
        'pku-hierarchy-view',
        'team',
        localized(
          'PKU层级视图',
          'PKU層級視圖',
          'PKU Hierarchy View',
          'PKU階層ビュー',
        ),
        localized(
          '按 PKU-一级对象-二级专户展示管理层级，支持查看专户主档与项目归属信息。',
          '按 PKU-一級對象-二級專戶展示管理層級，支持查看專戶主檔與項目歸屬信息。',
          'Show the PKU hierarchy as root, first-level objects, and second-level accounts, with account master and project attribution details.',
          'PKU-第1階層オブジェクト-第2階層専戸の階層を表示し、専戸マスタとプロジェクト帰属情報を確認できます。',
        ),
        './fams/pku-hierarchy-view',
        true,
      ),
      createPage(
        'assetAssumptions',
        'asset-assumptions',
        'experiment',
        localized('资产假设', '資產假設', 'Asset Assumptions', '資産前提'),
        localized(
          '管理各类资产的收益率、风险、波动率、相关性等关键假设参数，为配置测算和模型分析提供输入。',
          '管理各類資產的收益率、風險、波動率、相關性等關鍵假設參數，為配置測算和模型分析提供輸入。',
          'Configure return, risk, volatility, and correlation assumptions that feed allocation models and simulations.',
          '各資産の収益率、リスク、ボラティリティ、相関などの前提値を管理し、配分計算やモデル分析に利用します。',
        ),
      ),
      createPage(
        'portfolioOptimization',
        'portfolio-optimization',
        'deploymentUnit',
        localized(
          '组合构建与优化',
          '組合構建與優化',
          'Portfolio Construction & Optimization',
          'ポートフォリオ構築と最適化',
        ),
        localized(
          '支持基于投资目标、风险承受能力和约束条件构建组合，并进行收益风险分析与配置优化。',
          '支援基於投資目標、風險承受能力和約束條件構建組合，並進行收益風險分析與配置優化。',
          'Build portfolios around objectives, risk tolerance, and constraints, then compare return-risk trade-offs.',
          '投資目標、リスク許容度、制約条件に基づいてポートフォリオを構築し、最適化分析を行います。',
        ),
      ),
      createPage(
        'backtestingSimulation',
        'backtesting-simulation',
        'history',
        localized(
          '模拟回测',
          '模擬回測',
          'Backtesting Simulation',
          'バックテスト',
        ),
        localized(
          '利用历史数据对配置方案或投资组合进行模拟回测，评估不同策略在历史区间内的表现。',
          '利用歷史數據對配置方案或投資組合進行模擬回測，評估不同策略在歷史區間內的表現。',
          'Simulate historical performance for allocation plans or portfolios to compare strategy behavior across time.',
          '過去データを使って配分案やポートフォリオをバックテストし、戦略の歴史的な挙動を評価します。',
        ),
      ),
      createPage(
        'stressTesting',
        'stress-testing',
        'thunderbolt',
        localized('压力测试', '壓力測試', 'Stress Testing', 'ストレステスト'),
        localized(
          '在牛市、熊市、震荡市或自定义情景下模拟组合表现，评估极端市场环境下的风险承受能力。',
          '在牛市、熊市、震盪市或自定義情景下模擬組合表現，評估極端市場環境下的風險承受能力。',
          'Test portfolio resilience under bullish, bearish, sideways, or custom stress scenarios.',
          '強気・弱気・レンジ相場や任意シナリオでポートフォリオ挙動を再現し、耐性を確認します。',
        ),
      ),
      createPage(
        'lookThroughAnalysis',
        'look-through-analysis',
        'partition',
        localized(
          '穿透分析',
          '穿透分析',
          'Look-through Analysis',
          'ルックスルー分析',
        ),
        localized(
          '支持从一级 FOF 到管理人、再到底层资产的多层穿透展示，帮助用户看清真实资产结构。',
          '支援從一級 FOF 到管理人、再到底層資產的多層穿透展示，幫助用戶看清真實資產結構。',
          'Trace exposures from top-level funds through managers down to underlying assets.',
          '一次FOFから運用会社、最終的な底層資産まで多層的に掘り下げ、実質的な資産構造を可視化します。',
        ),
      ),
      createPage(
        'realTimeValuation',
        'real-time-valuation',
        'stock',
        localized(
          '实时估值',
          '實時估值',
          'Real-time Valuation',
          'リアルタイム評価',
        ),
        localized(
          '展示最新估值结果、估值变化和估值时点信息，为配置分析和风险管理提供及时数据支撑。',
          '展示最新估值結果、估值變化和估值時點資訊，為配置分析和風險管理提供及時數據支撐。',
          'Surface the latest valuations, valuation changes, and timestamps for allocation and risk decisions.',
          '最新の評価結果、評価変動、評価時点を確認し、配分分析とリスク管理を支援します。',
        ),
        './fams/real-time-valuation',
      ),
    ],
  ),
  createSection(
    'portfolioManagement',
    'portfolio-management',
    'wallet',
    localized(
      '组合管理',
      '組合管理',
      'Portfolio Management',
      'ポートフォリオ管理',
    ),
    [
      createPage(
        'productManagement',
        'product-management',
        'database',
        localized('产品管理', '產品管理', 'Product Management', '商品管理'),
        localized(
          '维护投资产品基础信息，为组合分析、经营总览、资产配置和报表统计提供统一产品主数据。',
          '維護投資產品基礎資訊，為組合分析、經營總覽、資產配置和報表統計提供統一產品主資料。',
          'Maintain core investment product data for portfolio analytics, operations reporting, allocation analysis, and downstream reporting.',
          '投資商品の基礎情報を整備し、ポートフォリオ分析、経営サマリー、資産配分、各種レポートの共通マスタとして利用します。',
        ),
        './fams/product-management',
        true,
      ),
      createPage(
        'productAnalysisView',
        'product-analysis-view',
        'table',
        localized(
          '产品分析视图',
          '產品分析視圖',
          'Product Analysis View',
          '商品分析ビュー',
        ),
        localized(
          '只读展示配置层与穿透管理人层并行口径，支持按日期、产品、一级类别与管理人筛选，服务统计分析。',
          '只讀展示配置層與穿透管理人層並行口徑，支援按日期、產品、一級類別與管理人篩選，服務統計分析。',
          'Read-only parallel view for configuration and manager look-through lenses, with filters by date, product, category, and manager.',
          '設定レイヤーと運用会社ルックスルーの並行ビューを読み取り専用で表示し、日付・商品・分類・運用会社で絞り込みます。',
        ),
        './fams/product-analysis-view',
      ),
      createPage(
        'portfolioOverview',
        'portfolio-overview',
        'profile',
        localized('收益总览', '收益總覽', 'Income Overview', '収益サマリー'),
        localized(
          '以专户收益汇总为核心，展示本金、市值、浮盈、已实现收益及趋势变化，是查看组合经营结果的统一入口。',
          '以專戶收益彙總為核心，展示本金、市值、浮盈、已實現收益及趨勢變化，是查看組合經營結果的統一入口。',
          'Use account income snapshots to review principal, market value, unrealized PnL, realized income, and recent trends.',
          '専戸収益集計を中心に、元本、時価、含み損益、実現収益、推移を確認する収益結果ページです。',
        ),
        './fams/portfolio-overview',
      ),
      createPage(
        'holdingAnalysis',
        'holding-analysis',
        'bars',
        localized('持仓分析', '持倉分析', 'Holdings Analysis', '保有分析'),
        localized(
          '以专户持仓表为核心，展示资产桶分布、资产类型结构、重点持仓和证券级持仓明细。',
          '以專戶持倉表為核心，展示資產桶分佈、資產類型結構、重點持倉和證券級持倉明細。',
          'Use account holdings to analyze asset buckets, asset-type structure, top positions, and security-level holdings.',
          '専戸持倉表を中心に、資産バケット、資産タイプ構成、主要保有、証券明細を分析します。',
        ),
        './fams/holding-analysis',
      ),
      createPage(
        'returnAnalysis',
        'return-analysis',
        'rise',
        localized('收益分析', '收益分析', 'Return Analysis', '収益分析'),
        localized(
          '展示区间收益汇总、资金占用收益率及收益率测算结果，帮助用户理解组合收益表现和变化趋势。',
          '展示區間收益彙總、資金占用收益率及收益率測算結果，幫助用戶理解組合收益表現和變化趨勢。',
          'Show period return summaries, capital-occupancy yields, and return-rate calculations to explain portfolio performance and trend changes.',
          '期間収益の集計、資金占用利回り、各種収益率の測定結果を表示し、ポートフォリオ収益の推移を把握できるようにします。',
        ),
        './fams/return-analysis',
      ),
      createPage(
        'tradeAnalysis',
        'trade-analysis',
        'swap',
        localized('交易分析', '交易分析', 'Trade Analysis', '取引分析'),
        localized(
          '分析区间内资金流转、投出回收、现金余额变化及其对组合收益与资金占用的影响。',
          '分析區間內資金流轉、投出回收、現金餘額變化及其對組合收益與資金占用的影響。',
          'Analyze capital movements, outbound and recovered investments, cash balance changes, and their impact on return and capital occupation.',
          '対象期間の資金移動、投下・回収、現金残高の変化、およびポートフォリオ収益と資金占用への影響を分析します。',
        ),
        './fams/trade-analysis',
      ),
      createPage(
        'performanceAttribution',
        'performance-attribution',
        'audit',
        localized(
          '收益归因',
          '收益歸因',
          'Performance Attribution',
          '収益要因分析',
        ),
        localized(
          '基于不同归因方法拆解组合收益来源，解释收益形成机制和结构贡献。',
          '基於不同歸因方法拆解組合收益來源，解釋收益形成機制和結構貢獻。',
          'Break down portfolio returns with attribution models to explain where performance came from.',
          '複数のアトリビューション手法で収益源泉を分解し、成果の成り立ちを説明します。',
        ),
      ),
      createPage(
        'managerPostInvestment',
        'manager-post-investment',
        'team',
        localized(
          '管理人投后',
          '管理人投後',
          'Manager Post-investment',
          '投資後管理',
        ),
        localized(
          '围绕管理人开展投后跟踪和评价，包括业绩表现、风格特征、交流记录和阶段性复盘。',
          '圍繞管理人開展投後跟蹤和評價，包括業績表現、風格特徵、交流記錄和階段性復盤。',
          'Track external managers after investment through performance, style, meeting records, and review notes.',
          '運用会社に対する投資後フォローとして、成績、運用スタイル、面談記録、定期レビューを管理します。',
        ),
      ),
      createPage(
        'assetSegmentView',
        'asset-segment-view',
        'block',
        localized(
          '细分资产视图',
          '細分資產視圖',
          'Asset Segment View',
          '資産別ビュー',
        ),
        localized(
          '按股票、债券、基金等细分类别查看组合表现，支持从整体组合下钻到具体资产维度。',
          '按股票、債券、基金等細分類別查看組合表現，支援從整體組合下鑽到具體資產維度。',
          'Slice portfolio performance by equities, bonds, funds, and other asset segments.',
          '株式、債券、ファンドなどの細分類別でポートフォリオを深掘り表示します。',
        ),
        './fams/asset-segment-view',
      ),
    ],
  ),
  createSection(
    'riskManagement',
    'risk-management',
    'safety',
    localized('风险管理', '風險管理', 'Risk Management', 'リスク管理'),
    [
      createPage(
        'riskOverview',
        'risk-overview',
        'dashboard',
        localized('风险总览', '風險總覽', 'Risk Overview', 'リスク概況'),
        localized(
          '集中展示组合整体风险水平、重点风险事项、核心风险因子变化和预警状态，是风险管理的总入口。',
          '集中展示組合整體風險水準、重點風險事項、核心風險因子變化和預警狀態，是風險管理的總入口。',
          'Use the main risk entry to monitor overall risk levels, critical items, factor shifts, and alert status.',
          'ポートフォリオ全体のリスク水準、主要リスク項目、因子変化、アラート状況をまとめて確認します。',
        ),
      ),
      createPage(
        'overallRisk',
        'overall-risk',
        'radarChart',
        localized('整体风险', '整體風險', 'Overall Risk', '全体リスク'),
        localized(
          '从全组合层面展示年化波动率、VaR、最大回撤、贝塔、集中度和汇率风险等核心风险指标。',
          '從全組合層面展示年化波動率、VaR、最大回撤、貝塔、集中度和匯率風險等核心風險指標。',
          'Inspect annualized volatility, VaR, drawdown, beta, concentration, and FX exposure at the total portfolio level.',
          '年率換算ボラティリティ、VaR、最大ドローダウン、ベータ、集中度、為替リスクなどを確認します。',
        ),
      ),
      createPage(
        'categoryRisk',
        'category-risk',
        'dotChart',
        localized('分类风险', '分類風險', 'Category Risk', '分類別リスク'),
        localized(
          '从资产类别维度分析不同类别资产的风险暴露和风险贡献，支持多层次风险观察。',
          '從資產類別維度分析不同類別資產的風險暴露和風險貢獻，支援多層次風險觀察。',
          'Compare risk exposure and contribution by asset category across multiple layers.',
          '資産分類ごとのリスクエクスポージャーと寄与度を比較し、多層的に把握します。',
        ),
      ),
      createPage(
        'underlyingRisk',
        'underlying-risk',
        'bug',
        localized(
          '底层风险',
          '底層風險',
          'Underlying Risk',
          'ルックスルーリスク',
        ),
        localized(
          '针对穿透到底层后的资产开展风险分析，识别真实底层资产的集中风险、结构风险和异常暴露。',
          '針對穿透到底層後的資產開展風險分析，識別真實底層資產的集中風險、結構風險和異常暴露。',
          'Analyze looked-through holdings to identify true concentration, structural, and abnormal exposures.',
          'ルックスルー後の底層資産に対して、集中リスク、構造リスク、異常エクスポージャーを洗い出します。',
        ),
      ),
      createPage(
        'riskMonitoring',
        'risk-monitoring',
        'monitor',
        localized(
          '风险监控',
          '風險監控',
          'Risk Monitoring',
          'リスクモニタリング',
        ),
        localized(
          '实时监控资产价格、成交量、风险指标以及宏观和行业动态，帮助系统持续发现潜在问题。',
          '實時監控資產價格、成交量、風險指標以及宏觀和行業動態，幫助系統持續發現潛在問題。',
          'Monitor prices, turnover, risk indicators, and market developments in near real time.',
          '価格、出来高、リスク指標、マクロ・業界動向を継続監視し、潜在問題を早期発見します。',
        ),
      ),
      createPage(
        'riskAlerts',
        'risk-alerts',
        'alert',
        localized('风险预警', '風險預警', 'Risk Alerts', 'リスクアラート'),
        localized(
          '支持按规则触发风险阈值预警，并集中展示预警内容、触发条件和处置状态。',
          '支援按規則觸發風險閾值預警，並集中展示預警內容、觸發條件和處置狀態。',
          'Review rule-based alert triggers, trigger conditions, and handling status in one place.',
          'ルールに基づくアラート内容、発火条件、対応状況を集中的に確認します。',
        ),
        './fams/risk-alerts',
      ),
      createPage(
        'liquidityRisk',
        'liquidity-risk',
        'hourglass',
        localized('流动性风险', '流動性風險', 'Liquidity Risk', '流動性リスク'),
        localized(
          '分析组合和资产的流动性状况、变现能力、交易成本和市场深度，识别流动性不足问题。',
          '分析組合和資產的流動性狀況、變現能力、交易成本和市場深度，識別流動性不足問題。',
          'Evaluate liquidity, exit capacity, trading cost, and market depth for portfolios and assets.',
          'ポートフォリオと資産の流動性、換金能力、売買コスト、市場厚みを分析します。',
        ),
      ),
      createPage(
        'creditRisk',
        'credit-risk',
        'safetyCertificate',
        localized('信用风险', '信用風險', 'Credit Risk', '信用リスク'),
        localized(
          '针对债券和固定收益类资产开展信用评级、信用分析和信用预警管理。',
          '針對債券和固定收益類資產開展信用評級、信用分析和信用預警管理。',
          'Assess credit rating, credit quality, and alert management for bond and fixed-income exposures.',
          '債券や固定収益資産の格付け、信用分析、信用アラート管理を行います。',
        ),
      ),
    ],
  ),
  createSection(
    'researchCollaboration',
    'research-collaboration',
    'book',
    localized('研究协同', '研究協同', 'Research Collaboration', 'リサーチ連携'),
    [
      createPage(
        'managerResearch',
        'manager-research',
        'read',
        localized(
          '管理人研究',
          '管理人研究',
          'Manager Research',
          '運用会社リサーチ',
        ),
        localized(
          '维护管理人研究档案，包括立项报告、结项报告、年度总结、业绩数据和综合评价信息。',
          '維護管理人研究檔案，包括立項報告、結項報告、年度總結、業績數據和綜合評價資訊。',
          'Maintain manager research files, including initiation reports, closing reports, performance data, and evaluations.',
          '運用会社のリサーチファイルを管理し、起案報告、完了報告、年度総括、実績データ、総合評価を蓄積します。',
        ),
      ),
      createPage(
        'researchTopics',
        'research-topics',
        'tags',
        localized('研究主题', '研究主題', 'Research Topics', '研究テーマ'),
        localized(
          '围绕重点投资方向、研究专题和任务组织研究内容，支持主题化沉淀与协同管理。',
          '圍繞重點投資方向、研究專題和任務組織研究內容，支援主題化沉澱與協同管理。',
          'Organize research content by investment focus, thematic topics, and coordinated tasks.',
          '重点投資テーマやタスクごとに研究内容を整理し、継続的な蓄積と協働を支援します。',
        ),
      ),
      createPage(
        'researchReports',
        'research-reports',
        'fileText',
        localized('研究报告', '研究報告', 'Research Reports', '研究レポート'),
        localized(
          '统一管理内部研究报告和专题分析成果，支持检索、查看、归档和后续复用。',
          '統一管理內部研究報告和專題分析成果，支援檢索、查看、歸檔和後續復用。',
          'Manage internal research reports and thematic studies for search, review, archiving, and reuse.',
          '社内研究レポートやテーマ分析成果を一元管理し、検索・閲覧・保管・再利用を支えます。',
        ),
      ),
      createPage(
        'externalMaterials',
        'external-materials',
        'folderOpen',
        localized('外部资料', '外部資料', 'External Materials', '外部資料'),
        localized(
          '沉淀外部研究报告、附件资料和参考文件，形成内外结合的研究资料中心。',
          '沉澱外部研究報告、附件資料和參考文件，形成內外結合的研究資料中心。',
          'Collect third-party reports, attachments, and reference files into a shared research repository.',
          '外部レポート、添付資料、参考ファイルを蓄積し、内外の知見を統合した資料庫を形成します。',
        ),
      ),
      createPage(
        'communicationRecords',
        'communication-records',
        'message',
        localized('交流记录', '交流記錄', 'Communication Records', '面談記録'),
        localized(
          '记录与管理人、研究对象和合作机构的交流纪要，保留研究过程中的关键证据链。',
          '記錄與管理人、研究對象和合作機構的交流紀要，保留研究過程中的關鍵證據鏈。',
          'Capture meetings and exchanges with managers, research targets, and partners as part of the research trail.',
          '運用会社、調査対象、協力機関との面談記録を残し、研究過程の証跡を保全します。',
        ),
      ),
      createPage(
        'annualSummary',
        'annual-summary',
        'schedule',
        localized('年度总结', '年度總結', 'Annual Summary', '年度総括'),
        localized(
          '沉淀年度研究成果、阶段性判断和复盘结论，为下一阶段投资决策提供参考。',
          '沉澱年度研究成果、階段性判斷和復盤結論，為下一階段投資決策提供參考。',
          'Summarize annual research conclusions, interim judgments, and retrospectives for the next cycle.',
          '年間の研究成果、判断、振り返りを整理し、次期の投資判断に活かします。',
        ),
      ),
    ],
  ),
  createSection(
    'dataCenter',
    'data-center',
    'database',
    localized('数据中心', '數據中心', 'Data Center', 'データセンター'),
    [
      createPage(
        'dataOverview',
        'data-overview',
        'database',
        localized('数据总览', '數據總覽', 'Data Overview', 'データ概況'),
        localized(
          '展示系统数据资产全貌，包括数据源、估值表、更新状态、异常情况和整体质量概览。',
          '展示系統數據資產全貌，包括數據源、估值表、更新狀態、異常情況和整體質量概覽。',
          'Review data sources, valuation sheets, refresh status, anomalies, and quality at a glance.',
          'データソース、評価表、更新状況、異常、品質状況を俯瞰できる入口です。',
        ),
      ),
      createPage(
        'metricDefinitions',
        'metric-definitions',
        'calculator',
        localized('指标口径', '指標口徑', 'Metric Definitions', '指標定義'),
        localized(
          '统一维护总市值、浮盈、实现收益、收益率、风险指标等核心指标的定义、公式和计算边界。',
          '統一維護總市值、浮盈、實現收益、收益率、風險指標等核心指標的定義、公式和計算邊界。',
          'Define formulas and boundaries for core metrics such as market value, gains, returns, and risk measures.',
          '総時価、含み損益、実現収益、収益率、リスク指標など主要指標の定義と算出境界を管理します。',
        ),
        './fams/metric-definitions',
      ),
      createPage(
        'conceptDefinitions',
        'concept-definitions',
        'infoCircle',
        localized('概念定义', '概念定義', 'Concept Definitions', '概念定義'),
        localized(
          '维护市值、本金、浮盈、实现收益、IRR 等关键概念定义，避免不同角色理解不一致。',
          '維護市值、本金、浮盈、實現收益、IRR 等關鍵概念定義，避免不同角色理解不一致。',
          'Standardize concepts such as market value, principal, unrealized gains, realized income, and IRR.',
          '時価、元本、含み損益、実現収益、IRR などの基本概念を統一し、認識のずれを防ぎます。',
        ),
        './fams/concept-definitions',
      ),
      createPage(
        'valuationTableManagement',
        'valuation-table-management',
        'table',
        localized(
          '估值表管理',
          '估值表管理',
          'Valuation Table Management',
          '評価表管理',
        ),
        localized(
          '统一管理股、债、衍生品、私募、另类等估值表，支持查看、关联、下载和维护。',
          '統一管理股、債、衍生品、私募、另類等估值表，支援查看、關聯、下載和維護。',
          'Manage equity, bond, derivative, private, and alternative valuation sheets in one place.',
          '株式、債券、デリバティブ、私募、オルタナティブなどの評価表を一元管理します。',
        ),
      ),
      createPage(
        'dataCollection',
        'data-collection',
        'cloudDownload',
        localized('数据采集', '數據採集', 'Data Collection', 'データ収集'),
        localized(
          '管理内源数据、外源数据和多源数据的接入过程，支持自动采集与手工补录两种模式。',
          '管理內源數據、外源數據和多源數據的接入過程，支援自動採集與手工補錄兩種模式。',
          'Coordinate internal, external, and multi-source ingestion with automated and manual collection modes.',
          '内部・外部・複数ソースのデータ取込を管理し、自動収集と手動補完の両方に対応します。',
        ),
      ),
      createPage(
        'dataParsing',
        'data-parsing',
        'apartment',
        localized('数据解析', '數據解析', 'Data Parsing', 'データ解析'),
        localized(
          '负责估值表解析、字段映射、预解析和异常处理，是数据进入系统的关键加工环节。',
          '負責估值表解析、欄位映射、預解析和異常處理，是數據進入系統的關鍵加工環節。',
          'Handle parsing, field mapping, preprocessing, and exception handling before data enters the platform.',
          '評価表の解析、項目マッピング、前処理、異常処理を担うデータ加工の要所です。',
        ),
      ),
      createPage(
        'dataCleansing',
        'data-cleansing',
        'filter',
        localized(
          '数据清洗',
          '數據清洗',
          'Data Cleansing',
          'データクレンジング',
        ),
        localized(
          '对错误值、缺失值和异常值进行识别与处理，并统一格式标准，提高数据可用性。',
          '對錯誤值、缺失值和異常值進行識別與處理，並統一格式標準，提高數據可用性。',
          'Identify bad, missing, and abnormal values while normalizing formats for downstream use.',
          '誤値、欠損値、異常値を検出・処理し、形式を標準化して利用可能性を高めます。',
        ),
      ),
      createPage(
        'dataQuality',
        'data-quality',
        'checkCircle',
        localized('数据质量', '數據質量', 'Data Quality', 'データ品質'),
        localized(
          '围绕完整性、准确性、及时性和一致性开展数据质量监控，保障分析结果可信可用。',
          '圍繞完整性、準確性、及時性和一致性開展數據質量監控，保障分析結果可信可用。',
          'Monitor completeness, accuracy, timeliness, and consistency to keep analytics trustworthy.',
          '完全性、正確性、適時性、一貫性の観点から品質監視を行い、分析結果の信頼性を確保します。',
        ),
      ),
      createPage(
        'masterDataDictionary',
        'master-data-dictionary',
        'book',
        localized(
          '主数据字典',
          '主數據字典',
          'Master Data Dictionary',
          'マスターデータ辞書',
        ),
        localized(
          '统一维护主数据、资产标签、规则项、基础字典和公共配置，作为全系统标准层。',
          '統一維護主數據、資產標籤、規則項、基礎字典和公共配置，作為全系統標準層。',
          'Maintain master data, tags, rule items, and shared dictionaries as system-wide standards.',
          'マスターデータ、資産タグ、ルール項目、基礎辞書、共通設定を一元管理する標準レイヤーです。',
        ),
      ),
      createPage(
        'dataLineage',
        'data-lineage',
        'branches',
        localized('数据溯源', '數據溯源', 'Data Lineage', 'データ来歴'),
        localized(
          '支持从指标结果追溯到原始数据、估值表和处理过程，满足审计、复盘和解释需求。',
          '支援從指標結果追溯到原始數據、估值表和處理過程，滿足審計、復盤和解釋需求。',
          'Trace metrics back to source data, valuation sheets, and processing steps for audit and explanation.',
          '指標結果から元データ、評価表、処理履歴まで遡及し、監査や説明責任に対応します。',
        ),
      ),
    ],
  ),
  createSection(
    'reportCenter',
    'report-center',
    'fileText',
    localized('报表中心', '報表中心', 'Report Center', 'レポートセンター'),
    [
      createPage(
        'operatingReports',
        'operating-reports',
        'fileSearch',
        localized('经营报表', '經營報表', 'Operating Reports', '運営レポート'),
        localized(
          '输出面向管理层的综合经营分析报表，汇总资产规模、收益表现、结构变化和关键结论。',
          '輸出面向管理層的綜合經營分析報表，彙總資產規模、收益表現、結構變化和關鍵結論。',
          'Generate management-facing operating reports covering scale, return, structure shifts, and key conclusions.',
          '経営層向けに、資産規模、収益、構造変化、要点をまとめた総合レポートを出力します。',
        ),
      ),
      createPage(
        'allocationReports',
        'allocation-reports',
        'fileDone',
        localized('配置报表', '配置報表', 'Allocation Reports', '配分レポート'),
        localized(
          '输出资产配置结构、配置偏离、穿透分析和长期配置结果，服务配置分析与决策汇报。',
          '輸出資產配置結構、配置偏離、穿透分析和長期配置結果，服務配置分析與決策彙報。',
          'Produce allocation reports covering structure, drift, look-through results, and long-term allocation outcomes.',
          '資産配分構造、乖離、ルックスルー分析、長期配分結果をまとめ、意思決定資料として提供します。',
        ),
      ),
      createPage(
        'riskReports',
        'risk-reports',
        'fileProtect',
        localized('风险报表', '風險報表', 'Risk Reports', 'リスクレポート'),
        localized(
          '输出风险指标、风险暴露、预警事件和风险趋势分析结果，服务风控和投委会场景。',
          '輸出風險指標、風險暴露、預警事件和風險趨勢分析結果，服務風控和投委會場景。',
          'Package risk indicators, exposures, alert events, and trend analysis for risk control workflows.',
          'リスク指標、エクスポージャー、アラート、推移分析をまとめ、リスク管理や委員会報告に活用します。',
        ),
      ),
      createPage(
        'attributionReports',
        'attribution-reports',
        'audit',
        localized(
          '归因报表',
          '歸因報表',
          'Attribution Reports',
          '要因分析レポート',
        ),
        localized(
          '输出收益归因、风险归因和结构归因结果，帮助用户解释组合表现来源。',
          '輸出收益歸因、風險歸因和結構歸因結果，幫助用戶解釋組合表現來源。',
          'Explain portfolio outcomes with return, risk, and structural attribution reports.',
          '収益要因、リスク要因、構造要因を整理し、ポートフォリオ成果の背景を説明します。',
        ),
      ),
      createPage(
        'performanceReports',
        'performance-reports',
        'barChart',
        localized(
          '绩效报表',
          '績效報表',
          'Performance Reports',
          'パフォーマンスレポート',
        ),
        localized(
          '输出年度、三年、五年、成立以来等维度的绩效评价与对比分析结果。',
          '輸出年度、三年、五年、成立以來等維度的績效評價與對比分析結果。',
          'Generate performance evaluation and comparison reports for yearly and long-term periods.',
          '年度、3年、5年、設定来などの期間でパフォーマンス評価と比較分析を出力します。',
        ),
      ),
      createPage(
        'customReports',
        'custom-reports',
        'fileAdd',
        localized(
          '自定义报表',
          '自定義報表',
          'Custom Reports',
          'カスタムレポート',
        ),
        localized(
          '允许用户按主题、指标、时间区间和组合维度灵活组装报表内容，满足个性化输出需求。',
          '允許用戶按主題、指標、時間區間和組合維度靈活組裝報表內容，滿足個性化輸出需求。',
          'Compose reports flexibly by theme, metric, time range, and portfolio dimension.',
          'テーマ、指標、期間、ポートフォリオ軸を組み合わせて、用途別のレポートを柔軟に構成します。',
        ),
      ),
      createPage(
        'exportManagement',
        'export-management',
        'export',
        localized(
          '导出管理',
          '導出管理',
          'Export Management',
          'エクスポート管理',
        ),
        localized(
          '统一管理报表导出任务、导出记录和文件下载，支撑日常汇报和资料留档。',
          '統一管理報表導出任務、導出記錄和檔案下載，支撐日常彙報和資料留檔。',
          'Track export jobs, export history, and file downloads for routine reporting and archiving.',
          'レポート出力タスク、出力履歴、ファイルダウンロードを一元管理し、日常報告と保管を支えます。',
        ),
      ),
    ],
  ),
  createSection(
    'systemManagement',
    'system-management',
    'setting',
    localized('系统管理', '系統管理', 'System Management', 'システム管理'),
    [
      createPage(
        'userRole',
        'user-role',
        'team',
        localized('用户角色', '用戶角色', 'User Roles', 'ユーザーとロール'),
        localized(
          '维护系统用户、组织、岗位和角色信息，为权限分配和流程管理提供基础支撑。',
          '維護系統用戶、組織、崗位和角色資訊，為權限分配和流程管理提供基礎支撐。',
          'Maintain users, organizations, positions, and roles as the basis for permissions and process control.',
          'システム利用者、組織、役職、ロールを管理し、権限と業務フローの基盤を整えます。',
        ),
      ),
      createPage(
        'permissionManagement',
        'permission-management',
        'safetyCertificate',
        localized('权限管理', '權限管理', 'Permission Management', '権限管理'),
        localized(
          '配置菜单权限、页面权限、操作权限和数据权限，确保不同岗位访问边界清晰可控。',
          '配置選單權限、頁面權限、操作權限和數據權限，確保不同崗位訪問邊界清晰可控。',
          'Configure menu, page, action, and data permissions with clear access boundaries by role.',
          'メニュー、ページ、操作、データ権限を設定し、役割ごとのアクセス境界を明確にします。',
        ),
      ),
      createPage(
        'feishuConfiguration',
        'feishu-configuration',
        'api',
        localized(
          '飞书配置',
          '飛書配置',
          'Feishu Configuration',
          'Feishu 設定',
        ),
        localized(
          '配置飞书自建应用、工作台入口、小组件和集成参数，打通统一访问入口。',
          '配置飛書自建應用、工作台入口、小組件和整合參數，打通統一訪問入口。',
          'Configure Feishu apps, workbench entries, widgets, and integration parameters.',
          'Feishu 自社アプリ、ワークベンチ入口、ウィジェット、連携パラメータを設定します。',
        ),
      ),
      createPage(
        'singleSignOn',
        'single-sign-on',
        'lock',
        localized(
          '单点登录',
          '單點登錄',
          'Single Sign-On',
          'シングルサインオン',
        ),
        localized(
          '管理单点登录相关配置，实现飞书与 Web 门户之间的统一身份认证与免登录体验。',
          '管理單點登錄相關配置，實現飛書與 Web 門戶之間的統一身份認證與免登入體驗。',
          'Manage SSO settings between Feishu and the web portal for seamless authentication.',
          'Feishu と Web ポータル間の SSO 設定を管理し、統一認証とシームレスなログインを実現します。',
        ),
      ),
      createPage(
        'messageNotifications',
        'message-notifications',
        'bell',
        localized('消息通知', '消息通知', 'Message Notifications', '通知設定'),
        localized(
          '配置系统消息、风险预警通知、任务提醒和报表推送规则，支撑系统主动触达。',
          '配置系統消息、風險預警通知、任務提醒和報表推送規則，支撐系統主動觸達。',
          'Define message, alert, reminder, and report delivery rules for proactive system outreach.',
          'システム通知、リスクアラート、タスクリマインド、レポート配信のルールを設定します。',
        ),
      ),
      createPage(
        'approvalWorkflow',
        'approval-workflow',
        'deploymentUnit',
        localized(
          '审批流配置',
          '審批流配置',
          'Approval Workflow',
          '承認フロー設定',
        ),
        localized(
          '管理与飞书审批流衔接的业务流程，如数据确认、报告审批和风险事项上报。',
          '管理與飛書審批流銜接的業務流程，如數據確認、報告審批和風險事項上報。',
          'Configure business workflows connected to Feishu approvals, such as data confirmation and report sign-off.',
          'データ確認、レポート承認、リスク報告など Feishu 承認フロー連携の業務プロセスを管理します。',
        ),
      ),
      createPage(
        'ruleConfiguration',
        'rule-configuration',
        'control',
        localized('规则配置', '規則配置', 'Rule Configuration', 'ルール設定'),
        localized(
          '维护估值规则、标签规则、指标规则和系统业务参数，支撑系统灵活配置与扩展。',
          '維護估值規則、標籤規則、指標規則和系統業務參數，支撐系統靈活配置與擴展。',
          'Manage valuation, tagging, metric, and business rules to keep the platform configurable.',
          '評価ルール、タグルール、指標ルール、業務パラメータを管理し、柔軟な拡張を支えます。',
        ),
      ),
      createPage(
        'auditLogs',
        'audit-logs',
        'fileSearch',
        localized('审计日志', '審計日誌', 'Audit Logs', '監査ログ'),
        localized(
          '记录用户操作、数据变更、任务执行和异常处理日志，满足审计追踪与运维排查需求。',
          '記錄用戶操作、數據變更、任務執行和異常處理日誌，滿足審計追蹤與運維排查需求。',
          'Track user actions, data changes, job execution, and exception handling for audit and operations review.',
          '利用者操作、データ変更、タスク実行、異常処理のログを残し、監査と運用調査に対応します。',
        ),
      ),
    ],
  ),
];

export const famsMenuSections: FamsSectionDefinition[] = [
  createSection(
    'workbench',
    'workbench',
    'home',
    localized('工作台', '工作台', 'Workbench', 'ワークベンチ'),
    [
      createPage(
        'workbenchOverview',
        'operations-overview',
        'dashboard',
        localized('工作台', '工作台', 'Workbench', 'ワークベンチ'),
        localized(
          '工作台首页入口。',
          '工作台首頁入口。',
          'Workbench landing overview.',
          'ワークベンチのホーム入口です。',
        ),
        './fams/workbench-overview',
      ),
      createPage(
        'pkuHierarchyView',
        'pku-hierarchy-view',
        'apartment',
        localized(
          'PKU层级视图',
          'PKU層級視圖',
          'PKU Hierarchy View',
          'PKU階層ビュー',
        ),
        localized(
          '按 PKU-一级对象-二级专户展示管理层级，支持查看专户主档与项目归属信息。',
          '按 PKU-一級對象-二級專戶展示管理層級，支持查看專戶主檔與項目歸屬信息。',
          'Show the PKU hierarchy as root, first-level objects, and second-level accounts, with account master and project attribution details.',
          'PKU-第1階層オブジェクト-第2階層専戸の階層を表示し、専戸マスタとプロジェクト帰属情報を確認できます。',
        ),
        './fams/pku-hierarchy-view',
        true,
      ),
    ],
    true,
  ),
  createSection(
    'assetAllocation',
    'asset-allocation',
    'pieChart',
    localized('资产配置', '資產配置', 'Asset Allocation', '資産配分'),
    [
      createPage(
        'portfolioPanorama',
        'portfolio-panorama',
        'fundProjectionScreen',
        localized(
          '组合总览',
          '組合總覽',
          'Portfolio Overview',
          'ポートフォリオ総覧',
        ),
        localized(
          '展示组合基本信息、投资业绩、净值走势和一级资产配置，是资产配置域的默认入口。',
          '展示組合基本資訊、投資業績、淨值走勢和一級資產配置，是資產配置域的預設入口。',
          'Default asset-allocation landing page for portfolio basics, performance, NAV trend, and level-one allocation.',
          '基本情報、投資実績、基準価額推移、第1階層配分を俯瞰する資産配分の既定入口です。',
        ),
        './fams/portfolio-panorama',
      ),
      createPage(
        'allocationDashboard',
        'allocation-dashboard',
        'dotChart',
        localized('配置详情', '配置詳情', 'Allocation Details', '配分詳細'),
        localized(
          '反映当前持仓资产配置情况。',
          '反映當前持倉資產配置情況。',
          'Reflect current holdings allocation.',
          '現在の保有資産配分を表示します。',
        ),
        './fams/allocation-dashboard',
      ),
      createPage(
        'longTermPerformance',
        'long-term-performance',
        'lineChart',
        localized(
          '长期业绩',
          '長期業績',
          'Long-term Performance',
          '長期パフォーマンス',
        ),
        localized(
          '按近三年查看组合年度收益、实现收益、资产类别表现和历史配置比例。',
          '按近三年查看組合年度收益、實現收益、資產類別表現和歷史配置比例。',
          'Review three-year annual returns, realized income, asset-class performance, and historical allocation.',
          '直近3年間の年次収益、実現収益、資産クラス別実績、過去の配分を確認します。',
        ),
        './fams/long-term-performance',
      ),
      createPage(
        'allocationPlan',
        'allocation-plan',
        'compass',
        localized('模型测算', '模型測算', 'Model Simulation', 'モデル試算'),
        localized(
          '多套配置方案管理。',
          '多套配置方案管理。',
          'Manage multiple allocation plans.',
          '複数の配分プランを管理します。',
        ),
        './fams/allocation-plan',
      ),
      createPage(
        'investmentExecution',
        'investment-execution',
        'unorderedList',
        localized('投资执行', '投資執行', 'Investment Execution', '投資執行'),
        localized(
          '按产品树查看和补充资金流水，口径对齐资金管理台账。',
          '按產品樹查看和補充資金流水，口徑對齊資金管理臺賬。',
          'Review and complete capital flows by product tree.',
          '商品ツリーで資金フローを確認・補完します。',
        ),
        './fams/capital-flow-management',
      ),
    ],
  ),
  createSection(
    'portfolioManagement',
    'portfolio-management',
    'wallet',
    localized(
      '组合管理',
      '組合管理',
      'Portfolio Management',
      'ポートフォリオ管理',
    ),
    [
      createPage(
        'productManagement',
        'product-management',
        'wallet',
        localized(
          '组合管理',
          '組合管理',
          'Portfolio Management',
          'ポートフォリオ管理',
        ),
        localized(
          '产品信息增删查改。',
          '產品資訊增刪查改。',
          'CRUD for product records.',
          '商品情報のCRUDです。',
        ),
        './fams/product-management',
      ),
      createPage(
        'productTreeView',
        'product-tree-view',
        'apartment',
        localized(
          '产品树视图',
          '產品樹視圖',
          'Product Tree View',
          '商品ツリービュー',
        ),
        localized(
          '以可视化树画布展示产品层级，支持缩放、平移、搜索和父子关系调整。',
          '以可視化樹畫布展示產品層級，支援縮放、平移、搜尋和父子關係調整。',
          'Visualize product hierarchy on a canvas with zoom, pan, search, and parent-child adjustment.',
          '商品階層をキャンバスで表示し、ズーム、パン、検索、親子関係の調整に対応します。',
        ),
        './fams/product-management/tree-view',
        true,
      ),
    ],
    true,
  ),
  createSection(
    'managerDatabase',
    'manager-database',
    'team',
    localized(
      '管理人研究',
      '管理人研究',
      'Asset Manager Research',
      '運用会社リサーチ',
    ),
    [
      createPage(
        'managerOverview',
        'manager-overview',
        'fundProjectionScreen',
        localized(
          '管理人总览',
          '管理人總覽',
          'Manager Overview',
          '運用会社総覧',
        ),
        localized(
          '展示管理人数量、合作规模、生命周期分布和风险预警，是管理人研究域的默认入口。',
          '展示管理人數量、合作規模、生命週期分布和風險預警，是管理人研究域的預設入口。',
          'Default manager-research landing page for manager counts, AUM, lifecycle mix, and risk alerts.',
          '運用会社数、協力規模、ライフサイクル分布、リスクアラートを俯瞰する研究域の既定入口です。',
        ),
        './fams/manager-overview',
      ),
      createGroup(
        'managers',
        'team',
        localized('管理人', '管理人', 'Managers', '運用会社'),
        [
          createPage(
            'managerPool',
            'manager-pool',
            'inbox',
            localized('候选库', '候選庫', 'Candidate Pool', '候補庫'),
            localized(
              '维护尚未准入的管理人候选，支持建档、入库审批和发起跟进。',
              '維護尚未准入的管理人候選，支援建檔、入庫審批和發起跟進。',
              'Maintain pre-admission manager candidates, onboarding approvals, and follow-ups.',
              '未受入れの運用会社候補を登録し、承認とフォローを進めます。',
            ),
            './fams/manager-pool',
          ),
          createPage(
            'managerManagement',
            'manager-management',
            'team',
            localized(
              '合作管理人',
              '合作管理人',
              'Cooperating Managers',
              '協業運用会社',
            ),
            localized(
              '管理已准入和已投资的管理人台账，跟踪规模、风险和复审。',
              '管理已準入和已投資的管理人臺賬，跟蹤規模、風險和複審。',
              'Roster of admitted and invested managers with AUM, risk, and review tracking.',
              '受入れ済み・投資済み運用会社の台帳として規模、リスク、レビューを管理します。',
            ),
            './fams/manager-management',
          ),
        ],
      ),
      createPage(
        'myInvestmentManagers',
        'my-investment-managers',
        'contacts',
        localized('投资经理', '投資經理', 'Investment Managers', '運用担当者'),
        localized(
          '维护基金会关注、考察和合作经理，以及内部产品关系和轻量笔记。',
          '維護基金會關注、考察和合作經理，以及內部產品關係和輕量筆記。',
          'Track watched and cooperating managers with internal relations and notes.',
          '注目・調査・協業中の担当者、内部商品との関係、メモを管理します。',
        ),
        './fams/my-investment-managers',
      ),
      createPage(
        'dueDiligence',
        'due-diligence',
        'audit',
        localized(
          '研究跟进',
          '研究跟進',
          'Research Follow-ups',
          '調査フォロー',
        ),
        localized(
          '跨管理人跟踪尽调、路演、拜访和复核事项。',
          '跨管理人跟蹤盡調、路演、拜訪和複核事項。',
          'Track due diligence, roadshows, visits, and reviews across managers.',
          'デューデリジェンス、説明会、訪問、レビューを横断管理します。',
        ),
        './fams/due-diligence',
      ),
      createPage(
        'managerRiskAlerts',
        'risk-alerts',
        'warning',
        localized('风险预警', '風險預警', 'Risk Alerts', 'リスクアラート'),
        localized(
          '集中跟踪管理人的关键人员、业绩、规模和合规风险信号。',
          '集中跟蹤管理人的關鍵人員、業績、規模和合規風險信號。',
          'Monitor key-person, performance, AUM, and compliance signals across asset managers.',
          '運用会社の主要人材、運用実績、規模、コンプライアンスのリスク兆候を集中管理します。',
        ),
        './fams/manager-risk-alerts',
      ),
      createPage(
        'investmentManagerLibrary',
        'investment-manager-library',
        'search',
        localized(
          '投资经理库',
          '投資經理庫',
          'Investment Manager Library',
          'ファンドマネージャー検索',
        ),
        localized(
          '基于授权公开数据筛选、比较和发现主动权益投资经理。',
          '基於授權公開數據篩選、比較和發現主動權益投資經理。',
          'Discover and compare active-equity managers using licensed market data.',
          '許諾済み市場データからアクティブ株式運用担当者を検索・比較します。',
        ),
        './fams/investment-manager-library',
        true,
      ),
      createPage(
        'personManagement',
        'person-management',
        'team',
        localized('人员档案', '人員檔案', 'People Files', '人物档案'),
        localized(
          '维护管理人研究涉及的人员、团队、任职关系和执业资格。',
          '維護管理人研究涉及的人員、團隊、任職關係和執業資格。',
          'Maintain people, teams, employments, and professional qualifications.',
          '人員、チーム、在籍履歴、資格を管理します。',
        ),
        './fams/person-management',
        true,
      ),
      createPage(
        'masterDataImport',
        'master-data-import',
        'upload',
        localized(
          '主数据导入',
          '主數據導入',
          'Master Data Import',
          'マスターデータ取込',
        ),
        localized(
          '通过标准 Excel 批量维护管理人、人员、产品及其关系。',
          '通過標準 Excel 批量維護管理人、人員、產品及其關係。',
          'Import managers, people, products, and relationships from standard Excel workbooks.',
          '標準 Excel から運用会社、人物、商品、関係を取り込みます。',
        ),
        './fams/master-data-import',
        true,
      ),
      createPage(
        'mergeReview',
        'merge-review',
        'audit',
        localized('待确认关联', '待確認關聯', 'Pending Links', '未確認関連'),
        localized(
          '处理历史回填中无法唯一匹配的人员、管理人和产品关系。',
          '處理歷史回填中無法唯一匹配的人員、管理人和產品關係。',
          'Resolve ambiguous people, manager, and product links from historical backfill.',
          '履歴移行で一意に特定できなかった関連を確認します。',
        ),
        './fams/merge-review',
        true,
      ),
    ],
  ),
  createSection(
    'riskManagement',
    'risk-management',
    'safety',
    localized('风险管理', '風險管理', 'Risk Management', 'リスク管理'),
    [
      createPage(
        'riskOverview',
        'risk-overview',
        'dashboard',
        localized('风险总览', '風險總覽', 'Risk Overview', 'リスク概況'),
        localized(
          '集中展示整体风险水平、重点风险事项、核心风险因子变化和预警状态。',
          '集中展示整體風險水準、重點風險事項、核心風險因子變化和預警狀態。',
          'Monitor overall risk levels, key issues, factor shifts, and alerts.',
          '全体リスク水準、主要事項、因子変化、アラート状況を確認します。',
        ),
      ),
      createPage(
        'concentrationRisk',
        'concentration-risk',
        'fundProjectionScreen',
        localized(
          '集中度风险',
          '集中度風險',
          'Concentration Risk',
          '集中度リスク',
        ),
        localized(
          '分析单专户、单链路、单底层资产类别的集中风险。',
          '分析單專戶、單鏈路、單底層資產類別的集中風險。',
          'Analyze concentration risk by account, chain, and underlying asset category.',
          '専戸、チェーン、底層資産カテゴリ別の集中リスクを分析します。',
        ),
        './fams/concentration-risk',
      ),
      createPage(
        'lookThroughRisk',
        'look-through-risk',
        'partition',
        localized(
          '穿透风险',
          '穿透風險',
          'Look-through Risk',
          'ルックスルーリスク',
        ),
        localized(
          '识别穿透到底层后的结构风险、集中风险和异常暴露。',
          '識別穿透到底層後的結構風險、集中風險和異常暴露。',
          'Identify structural risk, concentration risk, and abnormal exposure after look-through.',
          '底層まで穿透した後の構造リスク、集中リスク、異常エクスポージャーを識別します。',
        ),
        './fams/look-through-risk',
      ),
      createPage(
        'riskDeviation',
        'risk-deviation',
        'warning',
        localized(
          '偏离与超限',
          '偏離與超限',
          'Deviation & Breaches',
          '乖離と超限',
        ),
        localized(
          '查看配置偏离、约束超限和相关预警明细。',
          '查看配置偏離、約束超限和相關預警明細。',
          'Review allocation drift, limit breaches, and related alerts.',
          '配分乖離、制約超過、関連アラートの明細を確認します。',
        ),
        './fams/risk-deviation',
      ),
      createPage(
        'stressTesting',
        'stress-testing',
        'thunderbolt',
        localized(
          '情景与压力测试',
          '情景與壓力測試',
          'Scenario & Stress Testing',
          'シナリオとストレステスト',
        ),
        localized(
          '模拟不同情景下特定层级对象和整体组合的影响。',
          '模擬不同情景下特定層級對象和整體組合的影響。',
          'Test hierarchy objects and the full portfolio under scenario and stress conditions.',
          '各階層オブジェクトと全体ポートフォリオをシナリオ・ストレス条件で検証します。',
        ),
      ),
    ],
  ),
  createSection(
    'researchCollaboration',
    'research-collaboration',
    'book',
    localized('研究协同', '研究協同', 'Research Collaboration', 'リサーチ連携'),
    [
      createPage(
        'researchTopics',
        'research-topics',
        'tags',
        localized(
          '研究协同',
          '研究協同',
          'Research Collaboration',
          'リサーチ連携',
        ),
        localized(
          '研究协同模块入口。',
          '研究協同模組入口。',
          'Entry for research collaboration.',
          'リサーチ連携モジュールの入口です。',
        ),
      ),
      createPage(
        'approvalCenter',
        'approvals',
        'audit',
        localized('审核中心', '審核中心', 'Approval Center', '承認センター'),
        localized(
          '集中处理产品、流水和管理人待审核申请。',
          '集中處理產品、流水和管理人待審核申請。',
          'Review pending product, cash-flow, and manager approvals.',
          '商品・資金フロー・運用会社の承認申請を集中処理します。',
        ),
        './fams/approvals',
      ),
    ],
  ),
  createSection(
    'dataManagement',
    'data-management',
    'database',
    localized('数据管理', '數據管理', 'Data Management', 'データ管理'),
    [
      createPage(
        'assetClassManagement',
        'asset-class-management',
        'cluster',
        localized(
          '资产类别管理',
          '資產類別管理',
          'Asset Class Management',
          '資産クラス管理',
        ),
        localized(
          '资产类别维护。',
          '資產類別維護。',
          'Maintain asset classes.',
          '資産クラスを管理します。',
        ),
        './fams/asset-class-management',
      ),
      createPage(
        'custodianManagement',
        'custodian-management',
        'bank',
        localized(
          '托管机构管理',
          '託管機構管理',
          'Custodian Management',
          '受託機関管理',
        ),
        localized(
          '托管机构主数据维护。',
          '託管機構主數據維護。',
          'Maintain custodian masters.',
          '受託機関マスタを管理します。',
        ),
        './fams/custodian-management',
      ),
      createPage(
        'rolePermission',
        'role-permission',
        'safetyCertificate',
        localized('角色权限', '角色權限', 'Roles & Permissions', 'ロール権限'),
        localized(
          '定义角色并分配模块审核与提交权限。',
          '定義角色並分配模組審核與提交權限。',
          'Define roles and assign module submit/review permissions.',
          'ロールを定義し、モジュールの申請・承認権限を割り当てます。',
        ),
        './fams/role-permission',
      ),
      createPage(
        'memberManagement',
        'member-management',
        'team',
        localized('成员管理', '成員管理', 'Member Management', 'メンバー管理'),
        localized(
          '给系统成员分配角色。',
          '給系統成員分配角色。',
          'Assign roles to tenant members.',
          'メンバーにロールを割り当てます。',
        ),
        './fams/member-management',
      ),
      createPage(
        'systemChangelog',
        'system-changelog',
        'profile',
        localized(
          '系统更新日志',
          '系統更新日誌',
          'System Changelog',
          'システム更新ログ',
        ),
        localized(
          '查看和发布系统功能更新、问题修复与体验优化。',
          '查看和發佈系統功能更新、問題修復與體驗優化。',
          'Review and publish product updates, fixes, and improvements.',
          'システムの機能更新、修正、改善を確認・公開します。',
        ),
        './fams/system-changelog',
      ),
    ],
  ),
];

export const getFamsSectionPath = (section: FamsSectionDefinition): string =>
  `/${section.slug}`;

export const getFamsPagePath = (
  section: FamsSectionDefinition,
  page: FamsPageDefinition,
): string => `${getFamsSectionPath(section)}/${page.slug}`;

export const getVisibleFamsPages = (
  section: FamsSectionDefinition,
): FamsPageDefinition[] =>
  getSectionPages(section).filter((page) => !page.hiddenInMenu);

export const getFirstAccessibleFamsPage = (
  section: FamsSectionDefinition,
): FamsPageDefinition =>
  getVisibleFamsPages(section)[0] ?? getSectionPages(section)[0];

export const FAMS_DEFAULT_PATH = getFamsPagePath(
  famsMenuSections[0],
  getFirstAccessibleFamsPage(famsMenuSections[0]),
);

export const getFamsPageMessageKey = (
  sectionKey: string,
  pageKey: string,
): string => `${sectionKey}.${pageKey}`;

export const getFamsPageTitleMessageId = (pageKey: string): string =>
  `pages.${pageKey}.title`;

export const getFamsPageSubtitleMessageId = (pageKey: string): string =>
  `pages.${pageKey}.subtitle`;

export const buildFamsMenuMessages = (
  locale: FamsLocale,
): Record<string, string> => {
  const messages: Record<string, string> = {};

  famsMenuSections.forEach((section) => {
    messages[`menu.${section.key}`] = section.title[locale];

    section.children.forEach((item) => {
      if (isFamsMenuGroup(item)) {
        messages[`menu.${section.key}.${item.key}`] = item.title[locale];
        item.children.forEach((page) => {
          messages[`menu.${section.key}.${item.key}.${page.key}`] =
            page.title[locale];
        });
        return;
      }

      messages[`menu.${section.key}.${item.key}`] = item.title[locale];
      if (section.flattenInMenu) {
        messages[`menu.${item.key}`] = item.title[locale];
      }
    });
  });

  return messages;
};

export const buildFamsPageMessages = (
  locale: FamsLocale,
): Record<string, string> => {
  const messages: Record<string, string> = {
    'pages.placeholder.comingSoon': localized(
      '页面内容建设中',
      '頁面內容建設中',
      'Page content is being prepared',
      'ページ内容を準備中です',
    )[locale],
    'pages.placeholder.description': localized(
      '当前页面已完成菜单、路由和国际化接入，后续业务内容可在此基础上继续扩展。',
      '當前頁面已完成選單、路由和國際化接入，後續業務內容可在此基礎上繼續擴展。',
      'This page is wired into navigation, routing, and i18n, and is ready for business content to be added later.',
      'このページはメニュー、ルーティング、多言語対応まで接続済みで、今後の業務実装をそのまま積み上げられます。',
    )[locale],
  };

  famsMenuSections.forEach((section) => {
    getSectionPages(section).forEach((page) => {
      const pageKey = getFamsPageMessageKey(section.key, page.key);
      messages[getFamsPageTitleMessageId(pageKey)] = page.title[locale];
      messages[getFamsPageSubtitleMessageId(pageKey)] = page.subtitle[locale];
    });
  });

  return messages;
};

export const findFamsPageByPath = (pathname: string): FamsPageMatch | null => {
  for (const section of famsMenuSections) {
    for (const page of getSectionPages(section)) {
      const path = getFamsPagePath(section, page);
      if (path === pathname) {
        return {
          section,
          page,
          pageKey: getFamsPageMessageKey(section.key, page.key),
          path,
        };
      }
    }
  }

  return null;
};

const createPageRoute = (
  section: FamsSectionDefinition,
  page: FamsPageDefinition,
): FamsRouteDefinition => ({
  path: getFamsPagePath(section, page),
  name: page.key,
  icon: page.icon,
  component: page.component ?? './fams/PlaceholderPage',
  hideInMenu: page.hiddenInMenu,
  access: page.access,
});

const createMenuItemRoutes = (
  section: FamsSectionDefinition,
): FamsRouteDefinition[] =>
  section.children.flatMap((item) => {
    if (!isFamsMenuGroup(item)) {
      return [createPageRoute(section, item)];
    }

    const visibleChildren = item.children.filter((page) => !page.hiddenInMenu);
    if (!visibleChildren.length) {
      return item.children.map((page) => createPageRoute(section, page));
    }

    return [
      {
        path: getFamsSectionPath(section),
        name: item.key,
        icon: item.icon,
        routes: item.children.map((page) => createPageRoute(section, page)),
      },
    ];
  });

export const createFamsRoutes = (): FamsRouteDefinition[] =>
  famsMenuSections.flatMap<FamsRouteDefinition>((section) => {
    const sectionPath = getFamsSectionPath(section);
    const defaultPagePath = getFamsPagePath(
      section,
      getFirstAccessibleFamsPage(section),
    );

    if (section.flattenInMenu) {
      return [
        {
          path: sectionPath,
          redirect: defaultPagePath,
          hideInMenu: true,
        },
        ...createMenuItemRoutes(section),
      ];
    }

    return [
      {
        path: sectionPath,
        name: section.key,
        icon: section.icon,
        routes: [
          {
            path: sectionPath,
            redirect: defaultPagePath,
          },
          ...createMenuItemRoutes(section),
        ],
      },
    ];
  });
